/* Biochemistry Calculator — 4 common biochem formulas (pure client-side math).
   Zero AI credits. Uses the deep-teal theme.
*/
import React, { useState } from 'react';
import { FlaskConical, Calculator, ArrowRight } from 'lucide-react';

const TEAL = '#0F766E';
const TEAL_ACTIVE = '#14B8A6';

function Panel({ title, icon: Icon, children, testid }) {
  return (
    <div className="rounded-xl bg-white border border-teal-200 p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: TEAL }}>
          <Icon size={18} color="white" />
        </div>
        <h4 className="font-semibold text-sm uppercase tracking-wider" style={{ color: TEAL }}>{title}</h4>
      </div>
      {children}
    </div>
  );
}

function Input({ label, value, onChange, unit, testid }) {
  return (
    <div className="flex flex-col">
      <label className="text-[11px] uppercase text-gray-600 mb-1">{label}{unit ? <span className="text-gray-400 ml-1">({unit})</span> : null}</label>
      <input
        type="number"
        step="any"
        value={value}
        onChange={e => onChange(e.target.value)}
        className="px-3 py-2 rounded-md border border-teal-200 text-sm font-mono focus:outline-none focus:border-teal-500"
        data-testid={testid}
      />
    </div>
  );
}

function Result({ label, value, unit, highlight = true }) {
  return (
    <div className="rounded-lg p-3 mt-3 flex items-center justify-between"
      style={{ background: highlight ? '#F0FDFA' : 'white', border: '1px solid #99F6E4' }}>
      <span className="text-xs text-gray-600 uppercase tracking-wider">{label}</span>
      <span className="font-mono text-base font-bold" style={{ color: TEAL }}>
        {isFinite(value) && !isNaN(value) ? Number(value).toFixed(4) : '—'}
        {unit && <span className="text-[10px] text-gray-500 ml-1">{unit}</span>}
      </span>
    </div>
  );
}

/* 1. Beer-Lambert: A = εcl → c = A / (ε × l) */
function BeerLambert() {
  const [A, setA] = useState('0.785');
  const [eps, setEps] = useState('12700');   // M⁻¹ cm⁻¹ (chl a at 663 nm)
  const [l, setL] = useState('1');
  const c = parseFloat(A) / (parseFloat(eps) * parseFloat(l));
  return (
    <Panel title="Beer-Lambert Law" icon={Calculator} testid="beer-lambert">
      <p className="text-xs text-gray-600 mb-3">
        <code className="bg-teal-50 px-1 rounded">A = ε × c × l</code> &nbsp; → &nbsp;
        <code className="bg-teal-50 px-1 rounded">c = A / (ε × l)</code>
      </p>
      <div className="grid grid-cols-3 gap-3">
        <Input label="Absorbance (A)" value={A} onChange={setA} unit="AU" testid="bl-a" />
        <Input label="Molar ext. coeff. (ε)" value={eps} onChange={setEps} unit="M⁻¹ cm⁻¹" testid="bl-eps" />
        <Input label="Path length (l)" value={l} onChange={setL} unit="cm" testid="bl-l" />
      </div>
      <Result label="Concentration (c)" value={c} unit="M" />
      <Result label="Concentration (µM)" value={c * 1e6} unit="µM" highlight={false} />
    </Panel>
  );
}

/* 2. Enzyme activity — µmol/min/mg protein from ΔA / min */
function EnzymeActivity() {
  const [dA, setDA] = useState('0.025');  // ΔA/min
  const [eps, setEps] = useState('6.22'); // mM⁻¹ cm⁻¹ (common for NADH 340 nm)
  const [l, setL] = useState('1');
  const [vol, setVol] = useState('3');    // mL reaction
  const [prot, setProt] = useState('0.2'); // mg protein in reaction
  // Activity = ΔA / (ε × l) × (V_total / V_sample_protein)
  // Result: µmol/min × (reaction volume / mg protein)
  const rate = parseFloat(dA) / (parseFloat(eps) * parseFloat(l)); // mM/min
  const umolPerMin = rate * parseFloat(vol);   // µmol/min in total reaction
  const activity = umolPerMin / parseFloat(prot); // µmol/min/mg protein
  return (
    <Panel title="Enzyme Activity (Spectrophotometric)" icon={Calculator} testid="enzyme-calc">
      <p className="text-xs text-gray-600 mb-3">
        <code className="bg-teal-50 px-1 rounded">Activity = ΔA / (ε·l) × V_rxn / mg_protein</code>
      </p>
      <div className="grid grid-cols-3 gap-3">
        <Input label="ΔA per min" value={dA} onChange={setDA} unit="AU/min" testid="enz-da" />
        <Input label="ε (extinction)" value={eps} onChange={setEps} unit="mM⁻¹ cm⁻¹" testid="enz-eps" />
        <Input label="Path length" value={l} onChange={setL} unit="cm" testid="enz-l" />
        <Input label="Reaction vol" value={vol} onChange={setVol} unit="mL" testid="enz-vol" />
        <Input label="Protein in rxn" value={prot} onChange={setProt} unit="mg" testid="enz-prot" />
      </div>
      <Result label="Specific activity" value={activity} unit="µmol/min/mg protein" />
      <Result label="Rate (µmol/min)" value={umolPerMin} unit="µmol/min" highlight={false} />
    </Panel>
  );
}

/* 3. Bradford protein — from standard curve slope */
function BradfordProtein() {
  const [A, setA] = useState('0.525');     // sample absorbance at 595 nm
  const [slope, setSlope] = useState('0.0451'); // AU per µg BSA
  const [blank, setBlank] = useState('0.032');
  const [dilution, setDilution] = useState('10'); // dilution factor
  // µg protein = (A - blank) / slope × dilution factor
  const ug = (parseFloat(A) - parseFloat(blank)) / parseFloat(slope) * parseFloat(dilution);
  return (
    <Panel title="Bradford Protein Assay" icon={Calculator} testid="bradford-calc">
      <p className="text-xs text-gray-600 mb-3">
        <code className="bg-teal-50 px-1 rounded">µg protein = (A_sample − A_blank) / slope × dilution factor</code>
      </p>
      <div className="grid grid-cols-2 gap-3">
        <Input label="Sample absorbance" value={A} onChange={setA} unit="AU @ 595 nm" testid="br-a" />
        <Input label="Blank absorbance" value={blank} onChange={setBlank} unit="AU" testid="br-blank" />
        <Input label="Standard slope" value={slope} onChange={setSlope} unit="AU/µg BSA" testid="br-slope" />
        <Input label="Dilution factor" value={dilution} onChange={setDilution} unit="×" testid="br-df" />
      </div>
      <Result label="Protein concentration" value={ug} unit="µg" />
    </Panel>
  );
}

/* 4. Arnon (1949) chlorophyll */
function ArnonChlorophyll() {
  const [a663, setA663] = useState('1.25');
  const [a645, setA645] = useState('0.52');
  const [a470, setA470] = useState('0.41');
  const [vol, setVol] = useState('10');     // extract volume (mL)
  const [fw, setFW] = useState('1');         // fresh weight (g)
  const x663 = parseFloat(a663), x645 = parseFloat(a645), x470 = parseFloat(a470);
  // Arnon: µg/mL in extract; multiplied by vol and divided by FW → µg/g FW then /1000 → mg/g
  const chlA = (12.7 * x663 - 2.69 * x645) * parseFloat(vol) / parseFloat(fw) / 1000;
  const chlB = (22.9 * x645 - 4.68 * x663) * parseFloat(vol) / parseFloat(fw) / 1000;
  const total = (20.2 * x645 + 8.02 * x663) * parseFloat(vol) / parseFloat(fw) / 1000;
  const car = ((x470 - 0.114 * chlA - 0.638 * chlB * 1000) / 7.54) * parseFloat(vol) / parseFloat(fw) / 1000;
  return (
    <Panel title="Arnon (1949) Chlorophyll & Carotenoids" icon={Calculator} testid="arnon-calc">
      <p className="text-xs text-gray-600 mb-3">
        <code className="bg-teal-50 px-1 rounded">Chl a = 12.7·A663 − 2.69·A645</code> &nbsp;
        <code className="bg-teal-50 px-1 rounded">Chl b = 22.9·A645 − 4.68·A663</code><br />
        <code className="bg-teal-50 px-1 rounded">Total = 20.2·A645 + 8.02·A663</code> &nbsp;
        <code className="bg-teal-50 px-1 rounded">Car = (A470 − 0.114·Chla − 0.638·Chlb)/7.54</code>
      </p>
      <div className="grid grid-cols-3 gap-3">
        <Input label="A663" value={a663} onChange={setA663} unit="AU" testid="ar-663" />
        <Input label="A645" value={a645} onChange={setA645} unit="AU" testid="ar-645" />
        <Input label="A470" value={a470} onChange={setA470} unit="AU" testid="ar-470" />
        <Input label="Extract volume" value={vol} onChange={setVol} unit="mL" testid="ar-vol" />
        <Input label="Fresh weight" value={fw} onChange={setFW} unit="g" testid="ar-fw" />
      </div>
      <Result label="Chlorophyll a" value={chlA} unit="mg/g FW" />
      <Result label="Chlorophyll b" value={chlB} unit="mg/g FW" highlight={false} />
      <Result label="Total chlorophyll" value={total} unit="mg/g FW" />
      <Result label="Chl a / Chl b ratio" value={chlA / chlB} unit="" highlight={false} />
    </Panel>
  );
}

export default function BiochemistryCalculator() {
  return (
    <div className="space-y-5" data-testid="biochem-calculator">
      <div className="rounded-xl p-5 text-white" style={{ background: `linear-gradient(135deg, ${TEAL}, ${TEAL_ACTIVE})` }}>
        <div className="flex items-center gap-3">
          <FlaskConical size={28} />
          <div>
            <h3 className="text-lg font-bold">Biochemistry Calculator</h3>
            <p className="text-xs opacity-90">Quick reference formulas for daily lab work — pure math, zero AI credits.</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <BeerLambert />
        <EnzymeActivity />
        <BradfordProtein />
        <ArnonChlorophyll />
      </div>

      <div className="rounded-xl p-4" style={{ background: '#F0FDFA', border: '1px solid #99F6E4' }}>
        <div className="text-xs uppercase tracking-wider font-bold mb-1" style={{ color: TEAL }}>Reference Methods</div>
        <ul className="text-xs space-y-0.5" style={{ color: '#134E4A' }}>
          <li>Beer, A. (1852) — Beer–Lambert absorbance law.</li>
          <li>Arnon, D.I. (1949). <i>Plant Physiol.</i> 24: 1–15 — chlorophyll formulas.</li>
          <li>Lichtenthaler, H.K. (1987). <i>Methods Enzymol.</i> 148: 350–382 — carotenoid formula.</li>
          <li>Bradford, M.M. (1976). <i>Anal. Biochem.</i> 72: 248–254 — protein quantification.</li>
        </ul>
      </div>
    </div>
  );
}
